import {
  CHAT_ATTACHMENT_LIMITS,
  CHAT_ATTACHMENT_REFUSAL_CODES,
  formatChatAttachmentSize,
} from '@hector/shared-types';
import { mapWithConcurrency } from '@/features/document-merge/lib/mapWithConcurrency';
import type { AttachedDocument } from '../types/websocket.types';

/**
 * Téléversements simultanés des pièces jointes d'un message.
 *
 * Chaque requête paie un coût fixe (vérification du jeton, dépôt sur le
 * stockage, écriture de la ligne) qui domine pour de petits fichiers : en série,
 * 50 documents faisaient attendre 50 fois ce coût avant que la question parte.
 * Pas plus de 3 : des envois multipart de front saturent le lien montant de
 * l'avocat (cf. `services/folderHierarchyService.ts`, incidents des 2026-06-15
 * et 2026-10-07), et le serveur garde chaque fichier en mémoire le temps de sa
 * requête.
 */
export const TURN_ATTACHMENT_UPLOAD_CONCURRENCY = 3;

/**
 * Pourquoi un fichier n'est pas parti avec son message.
 *
 * - `unsupported_format`, `file_too_large` : refus DÉFINITIFS du serveur.
 *   Renvoyer le même fichier échouera toujours ; l'avocat doit en apprendre la
 *   cause, pas être invité à réessayer.
 * - `file_changed` : le fichier a été enregistré sur le disque après avoir été
 *   joint (Word qui sauvegarde, synchro OneDrive). Chrome ne lit plus jamais ce
 *   `File` : le `fetch` meurt en « Failed to fetch » sans qu'aucun octet ne
 *   parte, et chaque nouvel essai fait pareil. Seul le joindre à nouveau répare.
 * - `retryable` : tout le reste (réseau, serveur, stockage). Un nouvel essai
 *   peut aboutir, le fichier revient donc dans le composeur.
 */
export type UnsentAttachmentCause = 'unsupported_format' | 'file_too_large' | 'file_changed' | 'retryable';

/** Un fichier resté hors du message, tel que le toast le présente à l'avocat. */
export interface UnsentAttachment {
  name: string;
  cause: UnsentAttachmentCause;
}

/** Issue du téléversement des pièces jointes d'un message. */
export interface TurnAttachmentUploadResult {
  /** Pièces téléversées, dans l'ordre où l'avocat les a jointes. */
  uploaded: AttachedDocument[];
  /** Fichiers correspondants, même ordre : ce que la bulle du message doit afficher. */
  sentFiles: File[];
  /** Fichiers qui n'ont pas pu être téléversés, avec l'erreur reçue et sa cause. */
  failed: Array<{ file: File; error: unknown; cause: UnsentAttachmentCause }>;
}

/**
 * Cause d'un échec de téléversement, lue dans le code d'erreur que la route de
 * dépôt rend pour ses refus définitifs (`CHAT_ATTACHMENT_REFUSAL_CODES`). Une
 * erreur sans ce code — coupure réseau, erreur 500, backend antérieur à ces
 * codes — est tenue pour passagère.
 *
 * Seul le CODE fait foi, jamais le statut HTTP : un 413 sans code ne vient pas
 * de la route (elle répond 400 `FILE_TOO_LARGE`) mais d'un intermédiaire, pour
 * un fichier que le composeur a déjà admis sous le plafond. L'annoncer « trop
 * lourd pour le chat » serait faux ; il est rendu comme tout échec sans cause connue.
 */
export function classifyAttachmentUploadFailure(error: unknown): UnsentAttachmentCause {
  if (typeof error !== 'object' || error === null) return 'retryable';
  const errorCode = 'errorCode' in error ? error.errorCode : undefined;
  if (errorCode === CHAT_ATTACHMENT_REFUSAL_CODES.unsupportedFormat) return 'unsupported_format';
  if (errorCode === CHAT_ATTACHMENT_REFUSAL_CODES.fileTooLarge) return 'file_too_large';
  return 'retryable';
}

/**
 * Le navigateur peut-il encore lire ce fichier ?
 *
 * Un échec réseau et un fichier modifié sur le disque donnent la même erreur
 * (`TypeError: Failed to fetch`) : seule une lecture les sépare. Chrome rejette
 * avec `NotReadableError` toute lecture d'un `File` dont le fichier a changé
 * depuis la sélection. Un octet suffit à le savoir.
 *
 * Incident #2378 (2026-10-21) : un avocat a vu trois fois « La connexion avec
 * Hector a été interrompue ». Render n'a reçu que le preflight `OPTIONS` de
 * chaque essai, jamais le `POST` ; la même pièce, jointe à nouveau, est partie.
 */
async function isFileStillReadable(file: File): Promise<boolean> {
  return file.slice(0, 1).arrayBuffer().then(() => true, () => false);
}

type Settled =
  | { file: File; ok: true; document: AttachedDocument }
  | { file: File; ok: false; error: unknown; cause: UnsentAttachmentCause };

/**
 * Téléverse les pièces jointes d'un message, trois de front, sans qu'un échec
 * n'arrête les autres (#2066) : auparavant le premier fichier refusé annulait
 * le message entier, et l'avocat recommençait tout.
 *
 * Ne lève jamais : chaque fichier finit dans `uploaded` ou dans `failed`.
 *
 * @param uploadOne Téléverse UN fichier ; son rejet range le fichier dans `failed`.
 * @param options.onFileSettled Appelé une fois par fichier, dès qu'il est réglé
 *   (réussi ou non) — pour faire tomber son sablier sans attendre les autres.
 */
export async function uploadTurnAttachments(
  files: readonly File[],
  uploadOne: (file: File) => Promise<AttachedDocument>,
  options: { onFileSettled?: (file: File) => void } = {},
): Promise<TurnAttachmentUploadResult> {
  // `mapWithConcurrency` s'arrête au premier rejet : la fonction passée ne
  // rejette donc jamais, elle rend l'échec comme une valeur.
  const settled = await mapWithConcurrency<File, Settled>(
    files,
    TURN_ATTACHMENT_UPLOAD_CONCURRENCY,
    async (file) => {
      try {
        return { file, ok: true, document: await uploadOne(file) };
      } catch (error) {
        const cause = classifyAttachmentUploadFailure(error);
        // Seul un échec sans cause connue peut venir du fichier lui-même : un
        // refus du serveur prouve que les octets sont partis.
        if (cause === 'retryable' && !(await isFileStillReadable(file))) {
          return { file, ok: false, error, cause: 'file_changed' };
        }
        return { file, ok: false, error, cause };
      } finally {
        options.onFileSettled?.(file);
      }
    },
    () => undefined,
  );

  const result: TurnAttachmentUploadResult = { uploaded: [], sentFiles: [], failed: [] };
  for (const entry of settled) {
    if (entry.ok) {
      result.uploaded.push(entry.document);
      result.sentFiles.push(entry.file);
    } else {
      result.failed.push({ file: entry.file, error: entry.error, cause: entry.cause });
    }
  }
  return result;
}

/** Au-delà, les fichiers d'une même cause sont comptés et non nommés : le message reste lisible. */
const MAX_NAMED_UNSENT_FILES = 3;

function listUnsentNames(names: readonly string[]): string {
  const named = names.slice(0, MAX_NAMED_UNSENT_FILES).map((name) => `« ${name} »`);
  const others = names.length - named.length;
  if (others > 0) return `${named.join(', ')} et ${others} autre${others > 1 ? 's' : ''}`;
  if (named.length === 1) return named[0] ?? '';
  return `${named.slice(0, -1).join(', ')} et ${named[named.length - 1]}`;
}

/** Phrase d'une cause, accordée au nombre de fichiers qu'elle concerne. */
function describeCause(
  cause: UnsentAttachmentCause,
  names: readonly string[],
  composerVisible: boolean,
): string {
  const several = names.length > 1;
  const documents = `${several ? 'Les documents' : 'Le document'} ${listUnsentNames(names)}`;
  if (cause === 'retryable') {
    const pasPuPartir = several ? "n'ont pas pu être envoyés" : "n'a pas pu être envoyé";
    if (!composerVisible) {
      return `${documents} ${pasPuPartir}. `
        + (several
          ? 'Vous les retrouverez dans la zone de saisie de leur conversation.'
          : 'Vous le retrouverez dans la zone de saisie de sa conversation.');
    }
    return `${documents} ${pasPuPartir}. `
      + (several
        ? 'Ils sont de nouveau dans la zone de saisie : vous pouvez les renvoyer.'
        : 'Il est de nouveau dans la zone de saisie : vous pouvez le renvoyer.');
  }

  const pasPartis = several ? "n'ont pas été envoyés" : "n'a pas été envoyé";
  if (cause === 'file_changed') {
    return `${documents} ${pasPartis} : `
      + (several
        ? 'ils ont été modifiés sur votre ordinateur depuis que vous les avez joints. '
          + 'Joignez-les de nouveau.'
        : "il a été modifié sur votre ordinateur depuis que vous l'avez joint. "
          + 'Joignez-le de nouveau.');
  }
  if (cause === 'unsupported_format') {
    return `${documents} ${pasPartis} : ${several ? 'leur' : 'son'} format n'est pas accepté en pièce jointe. `
      + (several ? 'Enregistrez-les en PDF pour les joindre.' : 'Enregistrez-le en PDF pour le joindre.');
  }
  const limite = formatChatAttachmentSize(CHAT_ATTACHMENT_LIMITS.maxFileSizeBytes);
  return `${documents} ${pasPartis} : ${several ? 'ils dépassent' : 'il dépasse'} ${limite}, `
    + "le poids maximal d'un document joint.";
}

/** Ordre des causes dans la phrase : ce que l'avocat peut refaire d'abord. */
const CAUSES_IN_ORDER: readonly UnsentAttachmentCause[] = ['retryable', 'file_changed', 'unsupported_format', 'file_too_large'];

/** Une phrase par cause présente parmi `causes`, dans l'ordre de `CAUSES_IN_ORDER`. */
function describeByCause(
  unsent: readonly UnsentAttachment[],
  causes: readonly UnsentAttachmentCause[],
  composerVisible: boolean,
): string[] {
  return CAUSES_IN_ORDER
    .filter((cause) => causes.includes(cause))
    .map((cause) => ({ cause, names: unsent.filter((file) => file.cause === cause).map((file) => file.name) }))
    .filter(({ names }) => names.length > 0)
    .map(({ cause, names }) => describeCause(cause, names, composerVisible));
}

/**
 * Phrase affichée à l'avocat quand son message est parti sans certains fichiers.
 *
 * Elle ne promet que ce qui est vrai (#2066) :
 * - un fichier en échec PASSAGER revient dans le composeur de sa conversation,
 *   et la phrase dit où le trouver — sous les yeux de l'avocat, ou dans sa
 *   conversation s'il en regarde une autre ;
 * - un fichier qui ne repartira pas tel quel (refus du serveur, ou fichier
 *   modifié sur le disque) ne revient pas : la phrase en donne la cause au lieu
 *   d'inviter à le renvoyer.
 *
 * @param unsent Fichiers non envoyés (au moins un), dans l'ordre où l'avocat les a joints.
 * @param sentCount Nombre de fichiers partis avec le message (au moins un —
 *   quand aucun ne part, le message entier est annulé et cette phrase ne sert pas).
 * @param options.composerVisible L'avocat regarde-t-il la conversation du
 *   message ? C'est dans SON composeur que reviennent les fichiers à renvoyer.
 */
export function describeUnsentAttachments(
  unsent: readonly UnsentAttachment[],
  sentCount: number,
  options: { composerVisible: boolean },
): string {
  const parCause = describeByCause(unsent, CAUSES_IN_ORDER, options.composerVisible);
  const sent = sentCount > 1
    ? `Les ${sentCount} autres documents sont bien partis.`
    : "L'autre document est bien parti.";
  return [...parCause, sent].join(' ');
}

/**
 * Phrase affichée quand AUCUN fichier n'est parti — le message est alors annulé
 * — et qu'au moins un ne repartira pas tel quel (refus du serveur, fichier
 * modifié sur le disque) : elle nomme ces fichiers et leur cause, pour que
 * l'avocat ne renvoie pas le même message à l'identique.
 *
 * Elle ne dit rien des échecs passagers, ni de l'endroit où sont les fichiers :
 * le brouillon est rendu par le composeur, sur un autre chemin.
 *
 * @returns `null` quand tous les échecs sont passagers : l'erreur reçue suffit.
 */
export function describeRefusedAttachments(unsent: readonly UnsentAttachment[]): string | null {
  const parCause = describeByCause(unsent, ['file_changed', 'unsupported_format', 'file_too_large'], false);
  if (parCause.length === 0) return null;
  return [...parCause, "Votre message n'est pas parti."].join(' ');
}

